import React, { useEffect, useRef, useState } from "react"
import { MaterialLayoutRenderer } from "@jsonforms/material-renderers"
import { withJsonFormsLayoutProps, useJsonForms } from "@jsonforms/react"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Hidden,
} from "@mui/material"

import { UText } from "../../../ui-component"
import { FormRenderMode } from "@/components/Form/types"

const UAccordionContainer = (props) => {
  const { uischema, schema, path, visible, renderers } = props
  const { core } = useJsonForms()
  const validationMode = core?.validationMode

  const containerRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(uischema.mode !== FormRenderMode.EDIT)
  const [disableAnimation, setDisableAnimation] = useState(false)
  const accordionPath = path ? `/${path}` : ""

  const isErrorInThisAccordion = (instancePath?: string) => {
    if (!accordionPath || !instancePath) return false
    return (
      instancePath === accordionPath ||
      instancePath.startsWith(`${accordionPath}/`)
    )
  }

  useEffect(() => {
    // Only auto-expand when the form is submitted with errors
    if (validationMode !== "ValidateAndShow" || expanded) return

    const hasErrorInsideAccordion = Boolean(
      core?.errors?.some((error) => isErrorInThisAccordion(error?.instancePath)) ||
      core?.additionalErrors?.some((error) =>
        isErrorInThisAccordion(error?.instancePath),
      ),
    )

    if (hasErrorInsideAccordion) {
      setDisableAnimation(true)
      setExpanded(true)
      return
    }

    // Wait a tick for child renderers to apply Mui-error classes
    const timer = setTimeout(() => {
      if (containerRef.current?.querySelector(".Mui-error")) {
        // Expand instantly (no animation) so the existing scroll in Form.tsx works
        setDisableAnimation(true)
        setExpanded(true)
      }
    }, 50)
    return () => clearTimeout(timer)
  }, [validationMode, expanded, core?.errors, core?.additionalErrors, accordionPath])

  const layoutProps = {
    elements: uischema.elements,
    schema: schema,
    path: path,
    direction: "column",
    visible: visible,
    uischema: uischema,
    renderers: renderers,
  }

  return (
    <Hidden xsUp={!visible}>
      <div ref={containerRef}>
        <Accordion
          expanded={expanded}
          onChange={(_, isExpanded) => {
            setDisableAnimation(false)
            setExpanded(isExpanded)
          }}
          TransitionProps={disableAnimation ? { timeout: 0 } : undefined}
          sx={{
            boxShadow: "none",
            "&.Mui-expanded": { marginTop: "0px" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ height: "24px", width: "24px" }} />}
            sx={{
              backgroundColor: "#EEE",
              marginBottom: "4px",
              minHeight: "40px",
              height: "40px",
              padding: "8px",
              my: "4px",
              "&.Mui-expanded": { minHeight: "40px", height: "40px" },
            }}
          >
            <UText
              variant={"caption"}
              sxProp={{
                fontWeight: 500,
                lineHeight: "24px",
                letterSpacing: "0.17px",
                textTransform: "uppercase",
                color: "rgba(0, 0, 0, 0.6)",
                margin: 0,
              }}
            >
              {uischema.label}
            </UText>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: "0px" }}>
            <MaterialLayoutRenderer {...layoutProps} />
          </AccordionDetails>
        </Accordion>
      </div>
    </Hidden>
  )
}

export default withJsonFormsLayoutProps(UAccordionContainer)
